/**
 * Endpoints de sincronización con sistemas externos:
 *   - GET ERP: refresca tareas pendientes (remitos) y recepciones pendientes
 *   - GET SNT: consulta transacciones pendientes / búsqueda específica de remito
 *
 * Mock: no inyecta data nueva (el seed ya tiene todo); solo registra el call
 * en el API log para que la traza sea visible.
 */

import { z } from 'zod';
import { RemitoSchema } from '@/api/contracts/remitos';
import { OperacionSchema } from '@/api/contracts/operaciones';
import { getDb } from '@/api/mock/db';
import { logApi } from '@/stores/apilog';
import { ApiError } from './auth';
import { puedeOperarEnUbicacion } from '@/domain/rbac';

const MOCK_LATENCY_MS = 250;
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// SINCRONIZAR CON ERP + SNT
// =============================================================================

export const SincronizarRequest = z.object({
  ubicacionId: z.string().min(1),
  /**
   * Usuario que dispara la sincronización. En el mock validamos que el usuario
   * tiene permiso para operar en `ubicacionId` (defense in depth para R1 de
   * la auditoría). En producción, el backend debe extraer el usuario del JWT
   * y NUNCA confiar en el `usuarioId` del cliente.
   */
  usuarioId: z.string().min(1).optional(),
});

export const SincronizarResponse = z.object({
  remitosPendientes: z.number().int().nonnegative(),
  recepcionesPendientes: z.number().int().nonnegative(),
  duracionMs: z.number().int().nonnegative(),
  ts: z.string().datetime(),
});

export type SincronizarRequest = z.infer<typeof SincronizarRequest>;
export type SincronizarResponse = z.infer<typeof SincronizarResponse>;

export async function sincronizarConErp(
  input: SincronizarRequest,
): Promise<SincronizarResponse> {
  const req = SincronizarRequest.parse(input);
  const inicio = Date.now();
  await delay(MOCK_LATENCY_MS);

  const db = getDb();

  // Defense in depth: validar que el usuario puede operar en la ubicación que
  // está sincronizando. Sin esto, un cliente podría inyectar otro ubicacionId.
  // (Auditoría R1 / GAP-2 paquete S.)
  if (req.usuarioId) {
    const usuario = db.usuarios.find((u) => u.id === req.usuarioId);
    if (!usuario) {
      throw new ApiError(`Usuario ${req.usuarioId} no encontrado`, 'USUARIO_NOT_FOUND');
    }
    if (!puedeOperarEnUbicacion(usuario, req.ubicacionId, db.ubicaciones)) {
      throw new ApiError(
        `Usuario sin permiso para operar en ubicación ${req.ubicacionId}`,
        'UBICACION_NOT_PERMITTED',
      );
    }
  }
  const remitosPendientes = db.remitos.filter(
    (r) => r.origenUbicacionId === req.ubicacionId && r.estadoEnPlataforma === 'pendiente_trazar',
  ).length;
  const recepcionesPendientes = db.operaciones.filter(
    (o) =>
      o.tipo === 'despacho_transferencia' &&
      o.destinoUbicacionId === req.ubicacionId &&
      o.anmat.estado === 'informada' &&
      !db.operaciones.some(
        (r) => r.tipo === 'recepcion' && r.opOrigenId === o.id && r.anmat.estado !== 'cancelada',
      ),
  ).length;

  const duracionMs = Date.now() - inicio;
  const ts = new Date().toISOString();

  // 2 calls en una sincronización: GET ERP + GET SNT
  logApi({
    sistema: 'erp',
    metodo: 'GET',
    endpoint: '/api/requests/buscar/tareas.json?tipo=TAE&solopendientes=1',
    status: 200,
    duracionMs,
    responseBody: { response: [], count: remitosPendientes },
  });
  logApi({
    sistema: 'snt',
    metodo: 'GET',
    endpoint: '/snt/getTransaccionesNoConfirmadas?destino=' + req.ubicacionId,
    status: 200,
    duracionMs: Math.floor(duracionMs * 1.2),
    responseBody: { transaccionesPendientes: recepcionesPendientes },
  });

  return SincronizarResponse.parse({
    remitosPendientes,
    recepcionesPendientes,
    duracionMs,
    ts,
  });
}

// =============================================================================
// BUSCAR REMITO EN SNT (fallback cuando el sync no lo trajo)
// =============================================================================

export const BuscarRemitoRequest = z.object({
  numero: z.string().min(1),
  /** GLN del que busca — el SNT solo devuelve remitos donde el GLN es destino. */
  glnBuscador: z.string().min(1),
  /** Usuario que dispara la búsqueda (defense in depth). */
  usuarioId: z.string().min(1).optional(),
});

export const BuscarRemitoResponse = z.object({
  /** Operación de despacho encontrada (que somos destino). */
  operacion: OperacionSchema,
  remito: RemitoSchema.nullable(),
});

export type BuscarRemitoRequest = z.infer<typeof BuscarRemitoRequest>;
export type BuscarRemitoResponse = z.infer<typeof BuscarRemitoResponse>;

export async function buscarRemitoEnSnt(
  input: BuscarRemitoRequest,
): Promise<BuscarRemitoResponse> {
  const req = BuscarRemitoRequest.parse(input);
  await delay(MOCK_LATENCY_MS);
  const db = getDb();

  // Defense in depth: validar que el GLN buscado es uno en el que el usuario
  // puede operar. (Auditoría R1 / GAP-2 paquete S.)
  if (req.usuarioId) {
    const usuario = db.usuarios.find((u) => u.id === req.usuarioId);
    if (!usuario) {
      throw new ApiError(`Usuario ${req.usuarioId} no encontrado`, 'USUARIO_NOT_FOUND');
    }
    const ubGln = db.ubicaciones.find((u) => u.gln === req.glnBuscador);
    if (!ubGln || !puedeOperarEnUbicacion(usuario, ubGln.id, db.ubicaciones)) {
      throw new ApiError(
        `Usuario sin permiso para buscar remitos del GLN ${req.glnBuscador}`,
        'UBICACION_NOT_PERMITTED',
      );
    }
  }

  // Normalizar el número (acepta "R-PPVV-NNNN" o "PPVV-NNNN" o solo dígitos)
  const normalizado = req.numero.replace(/^R-/i, '').trim();
  const remito = db.remitos.find(
    (r) => r.numero === normalizado || r.numero.includes(normalizado),
  );

  logApi({
    sistema: 'snt',
    metodo: 'GET',
    endpoint: '/snt/getTransaccionesNoConfirmadas?numero=' + normalizado,
    status: remito ? 200 : 404,
    requestBody: { numero: normalizado, gln: req.glnBuscador },
    responseBody: remito ? { encontrado: true, numero: remito.numero } : { encontrado: false },
  });

  if (!remito) {
    throw new ApiError(`Remito ${req.numero} no encontrado en el SNT`, 'REMITO_NOT_FOUND');
  }
  // Buscar la op de despacho asociada
  const op = db.operaciones.find(
    (o) => o.tipo === 'despacho_transferencia' && o.remitoNumero === remito.numero,
  );
  if (!op) {
    throw new ApiError(
      'Remito encontrado pero sin operación de despacho asociada (todavía no informada al SNT)',
      'OP_NOT_FOUND',
    );
  }
  // Validar que nosotros somos el destino
  const ubDestino = db.ubicaciones.find((u) => u.id === op.destinoUbicacionId);
  if (!ubDestino || ubDestino.gln !== req.glnBuscador) {
    throw new ApiError(
      `Este remito no es para tu ubicación (destino: ${ubDestino?.nombre ?? '—'})`,
      'OP_DESTINO_MISMATCH',
    );
  }
  // No mostrar si ya fue recepcionado
  const yaConfirmada = db.operaciones.some(
    (r) => r.tipo === 'recepcion' && r.opOrigenId === op.id && r.anmat.estado !== 'cancelada',
  );
  if (yaConfirmada) {
    throw new ApiError('Este remito ya fue recepcionado y confirmado', 'OP_ALREADY_RECEIVED');
  }

  return BuscarRemitoResponse.parse({ operacion: op, remito });
}
